import { SurveySection, Survey } from './surveyTypes';
import { Trial } from './trialTypes';
import { Enrollment } from './enrollmentTypes';
import { Submission } from './submissionTypes';
import { Evaluation } from './certificateTypes';
import { QuipUser } from './quipUserTypes';
import { Institute } from './instituteTypes';

/** Redux Review State type */
export interface ReviewState {
  assignedReviewsList?: AssignedReview[];
  selectedReviewTab?: number;
  extendedReview?: ExtendedReview;
}

/** Core Data Model */
export interface Review {
  id: number;
  lastUpdatedAt: string;
  payload: ReviewPayload;
  justification: string;
  reviewerId: number;
  submissionId: number;
  sectionJustifications: string[];
  reviewRequestId: number;
  extendedPayload: ExtendedPayload;
}

/** Core Data Model */
export interface ReviewRequest {
  id: number;
  assigneeId: number;
  trialId: number;
  closed: boolean;
  reviewSurvey: ReviewSurvey;
}

/** Core Model ExtendedReview */
export interface ExtendedReview {
  id: number;
  data: CommentReview;
  trialId: number;
}

export type CommentReview = {
  [section: string]: {
    text: string;
    key: string;
    options: {
      [code: string]: string;
    }
  }[];
};

type AnswerPayload = {
  type: string;
  answer: string;
}
export type ReviewChanges = {
  [section: string]: {
    questions: {
      [uuid: string]: {
        caseNumber: {
          [caseIndex: string]: {
            before: AnswerPayload;
            after: AnswerPayload;
          }
        }
      }
    }
  }
}

export type ReviewHistory = {
  createdAt: string; // ISO String,
  reviewerId: number,
  payload: ReviewChanges,
  reviewId: number,
};

type ReviewSurveySection = Omit<SurveySection, 'contextQuestions | id'>

export interface ReviewSurvey {
  version: number;
  minimumReviewScore: number;
  minimumReviewSectionScores: number[];
  sampleIdsNecessary: boolean;
  sections: ReviewSurveySection[];
}

/** ResponseType of quipusers/[id]/assignedReviews */
export type AssignedReview = ReviewRequest & {
  trial: Trial & {survey: Survey};
};

export type EnrollmentKeys = Pick<Enrollment, 'id'| 'sampleset' | 'attendeeId' | 'trialId' | 'createdAt' | 'groundTruthId'>;
export type UserKeys = Pick<QuipUser, 'surname' | 'firstname' | 'title' | 'id' | 'userCountry'>;
export type InstituteKeys = Pick<Institute, 'city' | 'name' | 'country'>;

/** ResponseType of ReviewRequest/[id]/submissions/[trialId] */
export interface ReviewSubmissionData {
  submissionsData: AssignedReviewSubmission[];
  reviewSurvey: ReviewSurvey;
}

export type AssignedReviewSubmission = {
  enrollment: EnrollmentKeys & {
    submission: Submission;
    evaluation: Evaluation;
  };
  quipUser: UserKeys;
  institute: InstituteKeys;
}

type ReviewPayload = {
  version: number;
  sections: ({
      questions: {
        [uuid: string]: {
          answers: ({
              type: string;
              answer: string;
            } | null)[];
        }
      };
    } | null)[];
}

export type ExtendedPayload = {
  sections: {
    [key: string]: {
      headline: string;
      comments: MultiComment[]
    };
  }[];
}

type MultiComment = {
  value: string;
  label: string;
  key: string;
}

/** Redux constants */

export const GET_ASSIGNED_REVIEWS_LIST = 'GET_ASSIGNED_REVIEWS_LIST';
export const SET_SELECTED_REVIEW_TAB = 'SET_SELECTED_TAB';
export const SET_EXTENDED_REVIEW = 'SET_EXTENDED_REVIEW';

/** Action Types */

type GetAssignedReviewsAction = {
  type: typeof GET_ASSIGNED_REVIEWS_LIST;
  payload: AssignedReview[];
}

type SetSelectedReviewTabAction = {
  type: typeof SET_SELECTED_REVIEW_TAB;
  payload: number;
}

type SetExtendedReviewAction = {
  type: typeof SET_EXTENDED_REVIEW;
  payload?: ExtendedReview;
}

export type ReviewActionType =
GetAssignedReviewsAction
| SetSelectedReviewTabAction
| SetExtendedReviewAction;
